
import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image_url: string | null;
  published_at: string;
  content_type: string;
}

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const NewsEvents = () => {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('cms_posts')
        .select('id, title, slug, excerpt, featured_image_url, published_at, content_type')
        .eq('status', 'published')
        .in('content_type', ['news', 'blog_post'])
        .order('published_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      // Convert numeric IDs to strings for compatibility
      const postsWithStringIds = data?.map(post => ({
        ...post,
        id: String(post.id)
      })) || [];

      setPosts(postsWithStringIds);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to load articles',
        description: 'Please try refreshing the page',
      });
    } finally {
      setLoading(false);
    }
  };

  // Test newsletter function
  const testNewsletter = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('article-notification-trigger', {
        body: {},
      });

      if (error) {
        console.error('Newsletter test error:', error);
        toast({
          variant: 'destructive',
          title: 'Newsletter test failed',
          description: 'Please check the console for details',
        });
        return;
      }

      console.log('Newsletter test result:', data);
      toast({
        title: 'Newsletter test completed!',
        description: 'Check the console for details',
      });
    } catch (error) {
      console.error('Newsletter test error:', error);
      toast({
        variant: 'destructive',
        title: 'Newsletter test failed',
        description: 'Please check the console for details',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-24 px-4 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
            <span className="text-gray-700">Loading articles...</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-amber-50/50 py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-serif font-medium text-earth-900 mb-4 text-center">
            News & Events
          </h1>
          
          {/* Test Newsletter Button - Hidden in production */}
          {process.env.NODE_ENV === 'development' && (
            <div className="text-center mb-8">
              <Button 
                onClick={testNewsletter}
                variant="outline"
                className="text-sm"
              >
                Test Newsletter
              </Button>
            </div>
          )}
          
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No articles available at the moment.</p>
              <p className="text-gray-500 mt-2">Please check back later for updates.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {posts.map((post) => (
                <Card key={post.id} className="overflow-hidden flex flex-col md:flex-row hover:shadow-md transition-shadow">
                  <div className="md:w-1/3 h-48 md:h-auto">
                    {post.featured_image_url ? (
                      <img 
                        src={post.featured_image_url} 
                        alt={post.title} 
                        className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                        <span className="text-amber-700 text-sm font-medium">No Image</span>
                      </div>
                    )}
                  </div>
                  <div className="md:w-2/3 flex flex-col">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xl">{post.title}</CardTitle>
                      <CardDescription>{formatDate(post.published_at)}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <p className="text-earth-700">
                        {post.excerpt || `Read more about ${post.title.toLowerCase()}...`}
                      </p>
                    </CardContent>
                    <CardFooter className="pt-0">
                      <Button variant="outline" className="w-full text-sm" asChild>
                        <Link to={`/news/${post.slug}`}>
                          Read More <ArrowRight size={16} className="ml-2" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NewsEvents;
